import { FC } from 'react';
import { Button, InlineLoading } from '@carbon/react';
import { Copy } from '@carbon/react/icons';

type ButtonKind = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface CloneButtonProps {
  /** Fired on click. The caller owns the clone itself and its in-flight flag. */
  onClick: () => void;
  /** True while the clone is running: shows a spinner and blocks a second click. */
  busy?: boolean;
  /** Overridden where the thing being cloned is not a scenario ("Clone Case"). */
  label?: string;
  /** Disabled for a reason other than being busy. */
  disabled?: boolean;
  kind?: ButtonKind;
  size?: ButtonSize;
  className?: string;
}

/**
 * The one clone control. ALWAYS LABELLED, which is the point of it existing:
 * the scenario forms had grown five copies of the same labelled button, while
 * the newer list pages shipped a bare Copy icon whose meaning was discoverable
 * only by hovering. An icon alone is a guess; the label is the affordance and
 * the icon is the decoration.
 *
 * `busy` is a PROP rather than internal state, because every caller already
 * holds the flag: the forms derive it from their clone atom's loadable
 * (`cloneState.state === 'loading'`) and the list rows keep their own
 * `useState`. A second copy here would be two sources of truth for one
 * boolean, and the button could disagree with the page that spawned it.
 *
 * It deliberately does not know how to clone anything. The forms navigate to
 * the new record; the list rows refresh the table and raise their own
 * notification on failure. Those are different jobs and stay with the caller.
 * @param {CloneButtonProps} props - Click handler, busy flag and appearance
 * @return {JSX.Element} A labelled clone button, showing a spinner while busy
 */
const CloneButton: FC<CloneButtonProps> = ({
  onClick,
  busy = false,
  label = 'Clone Scenario',
  disabled = false,
  kind = 'tertiary',
  size = 'md',
  className,
}): JSX.Element => (
  <Button
    kind={kind}
    size={size}
    renderIcon={Copy}
    disabled={disabled || busy}
    onClick={onClick}
    className={className}
  >
    {busy ? <InlineLoading description="Cloning…" /> : label}
  </Button>
);

export { CloneButton };
export type { CloneButtonProps };
